import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import apiClient from '../../utils/api';
import { AlertDialog, PromptDialog, ConfirmDialog } from '../CustomDialog';
import NotificationSettings from './NotificationSettings';
import AppHeader from '../Header/AppHeader';
import PWAInstallButton from '../PWAInstallButton';
import { LanguageSelectorCompact } from '../LanguageSelector';
import './Settings.css';

function Settings() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [inviteName, setInviteName] = useState('');
  const [connectedPractitioners, setConnectedPractitioners] = useState([]);
  const [practitionerForm, setPractitionerForm] = useState({
    practitioner_name: '',
    specialization: '',
    company_name: ''
  });
  const [affiliateProfile, setAffiliateProfile] = useState(null);
  const [affiliateForm, setAffiliateForm] = useState({
    payment_method: 'iban',
    iban: '',
    paypal_email: '',
    account_holder_name: ''
  });
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  
  // Dialog states
  const [alertDialog, setAlertDialog] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  const [settings, setSettings] = useState({
    audio_enabled: false,
    timezone: 'Europe/Berlin',
    morning_time: '07:00',
    evening_time: '21:00',
    morning_frequency_id: '',
    evening_frequency_id: '',
    reminders_enabled: false,
    proactive_messages_enabled: true,
    mood_on_switch_enabled: true,
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Always load subscription status first time
      if (!subscriptionStatus) {
        const subStatus = await apiClient.getSubscriptionStatus();
        setSubscriptionStatus(subStatus);
      }
      
      if (activeTab === 'general') {
        const [freqData, prefsData, proactivePrefs] = await Promise.all([
          apiClient.getFrequencies(),
          apiClient.getPreferences(),
          apiClient.getProactivePreference(),
        ]);
        setFrequencies(freqData);
        setSettings({
          audio_enabled: prefsData.audio_enabled || false,
          timezone: prefsData.timezone || 'Europe/Berlin',
          morning_time: prefsData.morning_time || '07:00',
          evening_time: prefsData.evening_time || '21:00',
          morning_frequency_id: prefsData.morning_frequency_id || '',
          evening_frequency_id: prefsData.evening_frequency_id || '',
          reminders_enabled: prefsData.reminders_enabled || false,
          proactive_messages_enabled: proactivePrefs.enabled !== undefined ? proactivePrefs.enabled : true,
          mood_on_switch_enabled: prefsData.mood_on_switch_enabled !== false, // Default true
        });
      } else if (activeTab === 'cards') {
        const [freqData, cardsData] = await Promise.all([
          apiClient.getFrequencies(),
          apiClient.getMyProxyCards(),
        ]);
        setFrequencies(freqData);
        setSubAccounts(cardsData); // Reuse state for cards
      } else if (activeTab === 'family') {
        const [invData, subData] = await Promise.all([
          apiClient.getMyInvitations(),
          apiClient.getSubAccounts(),
        ]);
        setInvitations(invData);
        setSubAccounts(subData);
      } else if (activeTab === 'practitioners') {
        const practData = await apiClient.getMyPractitioners();
        setConnectedPractitioners(practData);
      } else if (activeTab === 'affiliate') {
        const affData = await apiClient.getMyAffiliateProfile();
        setAffiliateProfile(affData);
        
        if (affData) {
          const [refData, earnData, payData] = await Promise.all([
            apiClient.getMyReferrals(),
            apiClient.getMyEarnings(),
            apiClient.getMyPayouts()
          ]);
          setReferrals(refData);
          setEarnings(earnData);
          setPayouts(payData);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
    setSuccess('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiClient.updatePreferences(settings);
      // Separately save proactive messages preference
      await apiClient.toggleProactiveMessages(settings.proactive_messages_enabled);
      setSuccess('Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!inviteName.trim()) {
      setAlertDialog({
        type: 'warning',
        message: 'Bitte gib einen Namen für die Einladung ein'
      });
      return;
    }

    try {
      const result = await apiClient.createInvitation(inviteName);
      setInvitations([...invitations, result]);
      setInviteName('');
      setAlertDialog({
        type: 'success',
        title: 'Einladung erstellt!',
        message: `Teile diesen Link:\n${window.location.origin}/register?invite=${result.code}`
      });
    } catch (err) {
      setAlertDialog({
        type: 'error',
        message: err.message || 'Fehler beim Erstellen der Einladung'
      });
    }
  };

  const handleRevokeInvitation = async (code) => {
    setConfirmDialog({
      title: 'Einladung widerrufen?',
      message: 'Möchtest du diese Einladung wirklich widerrufen?',
      confirmText: 'Ja, widerrufen',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await apiClient.revokeInvitation(code);
          setInvitations(invitations.filter(i => i.code !== code));
        } catch (err) {
          setAlertDialog({
            type: 'error',
            message: 'Fehler beim Widerrufen der Einladung'
          });
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleRemoveSubAccount = async (userId) => {
    setConfirmDialog({
      title: 'Sub-Account entfernen?',
      message: 'Möchtest du diesen Sub-Account wirklich entfernen?',
      confirmText: 'Ja, entfernen',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await apiClient.removeSubAccount(userId);
          setSubAccounts(subAccounts.filter(s => s.id !== userId));
        } catch (err) {
          setAlertDialog({
            type: 'error',
            message: 'Fehler beim Entfernen des Sub-Accounts'
          });
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleClaimCard = async () => {
    // Check subscription status
    if (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed) {
      setAlertDialog({
        type: 'warning',
        message: 'Deine Testphase ist abgelaufen. Bitte upgraden, um Proxy-Karten zu nutzen.'
      });
      return;
    }

    // Show prompt dialog
    setPromptDialog({
      title: 'Proxy-Karte hinzufügen',
      message: 'Gib den 6-stelligen Kartencode ein:',
      placeholder: 'z.B. ABC123',
      onConfirm: async (code) => {
        setPromptDialog(null);
        if (!code || code.length !== 6) {
          setAlertDialog({
            type: 'error',
            message: 'Der Code muss genau 6 Zeichen lang sein.'
          });
          return;
        }

        try {
          await apiClient.claimProxyCard(code);
          loadData();
          setAlertDialog({
            type: 'success',
            title: 'Erfolgreich!',
            message: 'Karte erfolgreich aktiviert!'
          });
        } catch (err) {
          setAlertDialog({
            type: 'error',
            message: err.message || 'Karte nicht gefunden oder bereits vergeben'
          });
        }
      },
      onCancel: () => setPromptDialog(null)
    });
  };

  const handleAssignFrequency = async (cardId, frequencyId) => {
    // Check subscription status
    if (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed) {
      setAlertDialog({
        type: 'warning',
        message: 'Deine Testphase ist abgelaufen. Bitte upgraden, um Proxy-Karten zu nutzen.'
      });
      return;
    }

    try {
      await apiClient.assignCardFrequency(cardId, frequencyId);
      loadData();
    } catch (err) {
      setAlertDialog({
        type: 'error',
        message: 'Fehler beim Zuweisen der Frequenz'
      });
    }
  };

  const handleRevokePractitionerAccess = async (connectionId, practitionerName) => {
    setConfirmDialog({
      title: 'Zugriff widerrufen?',
      message: `Möchtest du ${practitionerName} wirklich den Zugriff entziehen? Der Therapeut kann dann nicht mehr auf deine Daten zugreifen.`,
      confirmText: 'Ja, widerrufen',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await apiClient.revokePractitionerAccess(connectionId);
          loadData();
          setSuccess('Zugriff erfolgreich widerrufen');
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message || 'Fehler beim Widerrufen des Zugriffs');
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleBecomePractitioner = async () => {
    if (!practitionerForm.practitioner_name.trim()) {
      setError('Bitte gib einen Namen ein');
      return;
    }

    try {
      await apiClient.becomePractitioner(
        practitionerForm.practitioner_name,
        practitionerForm.specialization,
        practitionerForm.company_name
      );
      
      setSuccess('Therapeuten-Rolle erfolgreich aktiviert! 🎉');
      setTimeout(() => {
        window.location.reload(); // Reload to update user object
      }, 1500);
    } catch (err) {
      setError(err.message || 'Fehler beim Aktivieren der Therapeuten-Rolle');
    }
  };

  const handleBecomeAffiliate = async () => {
    if (!affiliateForm.account_holder_name.trim()) {
      setError('Bitte Kontoinhaber eingeben');
      return;
    }

    if (affiliateForm.payment_method === 'iban' && !affiliateForm.iban.trim()) {
      setError('Bitte IBAN eingeben');
      return;
    }

    if (affiliateForm.payment_method === 'paypal' && !affiliateForm.paypal_email.trim()) {
      setError('Bitte PayPal E-Mail eingeben');
      return;
    }

    try {
      await apiClient.becomeAffiliate(
        affiliateForm.payment_method,
        affiliateForm.iban,
        affiliateForm.paypal_email,
        affiliateForm.account_holder_name
      );
      
      setSuccess('Affiliate-Partner Registrierung erfolgreich! 🎉 Ein Admin wird dir bald eine Kategorie zuweisen.');
      setTimeout(() => {
        loadData();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Fehler bei der Registrierung');
    }
  };

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    
    if (!amount || amount <= 0) {
      setError('Bitte gültigen Betrag eingeben');
      return;
    }

    try {
      await apiClient.requestPayout(amount);
      setShowPayoutModal(false);
      setPayoutAmount('');
      setSuccess('Auszahlung beantragt! Ein Admin wird deine Anfrage bearbeiten.');
      setTimeout(() => {
        setSuccess('');
        loadData();
      }, 3000);
    } catch (err) {
      setError(err.message || 'Fehler bei der Auszahlungsanfrage');
    }
  };


  const handleReleaseCard = async (cardId) => {
    setConfirmDialog({
      title: 'Karte freigeben?',
      message: 'Möchtest du diese Proxy-Karte wirklich freigeben? Sie kann dann von anderen Nutzern beansprucht werden.',
      confirmText: 'Ja, freigeben',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await apiClient.releaseMyCard(cardId);
          loadData();
          setAlertDialog({
            type: 'success',
            message: 'Karte erfolgreich freigegeben!'
          });
        } catch (err) {
          setAlertDialog({
            type: 'error',
            message: 'Fehler beim Freigeben der Karte'
          });
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToHistory = () => {
    navigate('/history');
  };

  const handleGoToNotes = () => {
    navigate('/notes');
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  const timezones = [
    'Europe/Berlin',
    'Europe/Vienna',
    'Europe/Zurich',
    'Europe/London',
    'Europe/Paris',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Dubai',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{isGerman ? 'Lade Einstellungen...' : 'Loading settings...'}</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container settings-dashboard">
        <main className="dashboard-main settings-main">
        
        {/* Submenu Navigation */}
        <div className="account-submenu">
          <button 
            className="submenu-item"
            onClick={() => navigate('/account')}
          >
            {isGerman ? 'Profil bearbeiten' : 'Edit Profile'}
          </button>
          <button 
            className="submenu-item"
            onClick={() => navigate('/subscription')}
          >
            {isGerman ? 'Mein Abo' : 'My Subscription'}
          </button>
          <button 
            className="submenu-item active"
            onClick={() => navigate('/settings')}
          >
            {t('common.settings')}
          </button>
        </div>
        
        {success && (
          <div className="success-message" data-testid="success-message">
            ✅ {success}
          </div>
        )}
        
        {error && (
          <div className="error-message" data-testid="error-message">
            ❌ {error}
          </div>
        )}

        {/* Settings Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            ⚙️ {isGerman ? 'Allgemein' : 'General'}
          </button>
          <button
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            data-testid="notifications-tab"
          >
            🔔 {isGerman ? 'Benachrichtigungen' : 'Notifications'}
          </button>
          {/* Temporarily hidden for launch
          <button
            className={`settings-tab ${activeTab === 'practitioners' ? 'active' : ''}`}
            onClick={() => setActiveTab('practitioners')}
          >
            🩺 {isGerman ? 'Meine Therapeuten' : 'My Therapists'}
          </button>
          */}
          <button
            className={`settings-tab ${activeTab === 'affiliate' ? 'active' : ''}`}
            onClick={() => setActiveTab('affiliate')}
          >
            💸 {isGerman ? 'Affiliate-Partner' : 'Affiliate Partner'}
          </button>
          <button
            className={`settings-tab ${activeTab === 'cards' ? 'active' : ''}`}
            onClick={() => setActiveTab('cards')}
          >
            🎴 {isGerman ? 'Meine Proxy-Karten' : 'My Proxy Cards'}
          </button>
          {subscriptionStatus?.subscription_plan?.includes('premium') && (
            <button
              className={`settings-tab ${activeTab === 'family' ? 'active' : ''}`}
              onClick={() => setActiveTab('family')}
            >
              👨‍👩‍👧‍👦 {isGerman ? 'Familie & Freunde' : 'Family & Friends'}
            </button>
          )}
        </div>

        {activeTab === 'notifications' && (
          <NotificationSettings />
        )}

        {activeTab === 'general' && (
          <div className="settings-container">
          {/* ZEN-ID Section */}
          <div className="settings-section">
            <h2 className="section-title">🆔 {isGerman ? 'Deine ZEN-ID' : 'Your ZEN-ID'}</h2>
            
            <div className="zen-id-container">
              <div className="zen-id-display">
                <span className="zen-id-label">{isGerman ? 'Deine eindeutige ID:' : 'Your unique ID:'}</span>
                <div className="zen-id-value" data-testid="zen-id-display">{user.zen_id || (isGerman ? 'Nicht verfügbar' : 'Not available')}</div>
              </div>
              <p className="zen-id-hint">
                💡 {isGerman 
                  ? 'Deine persönliche ZENTA-ID für dein Konto.' 
                  : 'Your personal ZENTA ID for your account.'}
              </p>
              
              {/* Temporarily hidden for launch
              {!user.is_practitioner && !user.roles?.includes('practitioner') && (
                <div className="become-practitioner-section">
                  <p className="practitioner-hint">
                    🩺 {isGerman 
                      ? 'Möchtest du selbst als Therapeut tätig werden und andere Menschen begleiten?' 
                      : 'Would you like to work as a therapist and accompany other people?'}
                  </p>
                  <button 
                    onClick={() => setActiveTab('practitioners')}
                    className="btn-become-practitioner"
                  >
                    {isGerman ? 'Als Therapeut registrieren' : 'Register as Therapist'}
                  </button>
                </div>
              )}
              */}
            </div>
          </div>

          {/* General Settings */}
          <div className="settings-section">
            <h2 className="section-title">🌍 {isGerman ? 'Allgemeine Einstellungen' : 'General Settings'}</h2>
            
            {/* Language Setting */}
            <div className="setting-item">
              <label>{isGerman ? 'Sprache' : 'Language'} / {isGerman ? 'Language' : 'Sprache'}</label>
              <LanguageSelectorCompact />
              <p className="setting-hint">{isGerman ? 'Wähle deine bevorzugte Sprache' : 'Choose your preferred language'}</p>
            </div>
            
            <div className="setting-item">
              <label htmlFor="timezone">{isGerman ? 'Zeitzone' : 'Timezone'}</label>
              <select
                id="timezone"
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                data-testid="timezone-select"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <p className="setting-hint">{isGerman ? 'Ihre Zeitzone für korrekte Routine-Zeiten' : 'Your timezone for correct routine times'}</p>
            </div>
          </div>

          {/* Daily Routine */}
          <div className="settings-section">
            <h2 className="section-title">🌅 {isGerman ? 'Tägliche Routine' : 'Daily Routine'}</h2>
            
            <div className="routine-grid">
              <div className="routine-card morning">
                <div className="routine-header">
                  <span className="routine-icon">☀️</span>
                  <h3>{isGerman ? 'Morgen-Routine' : 'Morning Routine'}</h3>
                </div>
                
                <div className="setting-item">
                  <label htmlFor="morning_time">{isGerman ? 'Uhrzeit' : 'Time'}</label>
                  <input
                    id="morning_time"
                    type="time"
                    value={settings.morning_time}
                    onChange={(e) => handleChange('morning_time', e.target.value)}
                    data-testid="morning-time-input"
                  />
                </div>

                <div className="setting-item">
                  <label htmlFor="morning_frequency">{isGerman ? 'Frequenz' : 'Frequency'}</label>
                  <select
                    id="morning_frequency"
                    value={settings.morning_frequency_id}
                    onChange={(e) => handleChange('morning_frequency_id', e.target.value)}
                    data-testid="morning-frequency-select"
                  >
                    <option value="">{isGerman ? 'Keine Frequenz' : 'No frequency'}</option>
                    {frequencies.map((freq) => (
                      <option key={freq.id} value={freq.id}>
                        {freq.icon} {freq.name}
                      </option>
                    ))}
                  </select>
                  <p className="setting-hint">
                    {isGerman 
                      ? `Diese Frequenz wird automatisch um ${settings.morning_time} Uhr aktiviert`
                      : `This frequency will be automatically activated at ${settings.morning_time}`}
                  </p>
                </div>
              </div>

              <div className="routine-card evening">
                <div className="routine-header">
                  <span className="routine-icon">🌙</span>
                  <h3>{isGerman ? 'Abend-Routine' : 'Evening Routine'}</h3>
                </div>
                
                <div className="setting-item">
                  <label htmlFor="evening_time">{isGerman ? 'Uhrzeit' : 'Time'}</label>
                  <input
                    id="evening_time"
                    type="time"
                    value={settings.evening_time}
                    onChange={(e) => handleChange('evening_time', e.target.value)}
                    data-testid="evening-time-input"
                  />
                </div>

                <div className="setting-item">
                  <label htmlFor="evening_frequency">{isGerman ? 'Frequenz' : 'Frequency'}</label>
                  <select
                    id="evening_frequency"
                    value={settings.evening_frequency_id}
                    onChange={(e) => handleChange('evening_frequency_id', e.target.value)}
                    data-testid="evening-frequency-select"
                  >
                    <option value="">{isGerman ? 'Keine Frequenz' : 'No frequency'}</option>
                    {frequencies.map((freq) => (
                      <option key={freq.id} value={freq.id}>
                        {freq.icon} {freq.name}
                      </option>
                    ))}
                  </select>
                  <p className="setting-hint">
                    {isGerman 
                      ? `Diese Frequenz wird automatisch um ${settings.evening_time} Uhr aktiviert`
                      : `This frequency will be automatically activated at ${settings.evening_time}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="settings-section">
            <h2 className="section-title">🔔 {isGerman ? 'Benachrichtigungen' : 'Notifications'}</h2>
            
            <div className="setting-item toggle-item">
              <div className="toggle-info">
                <label htmlFor="reminders">{isGerman ? 'Erinnerungen aktivieren' : 'Enable Reminders'}</label>
                <p className="setting-hint">
                  {isGerman 
                    ? 'Erhalte Browser-Benachrichtigungen für deine Morgen- und Abend-Routine'
                    : 'Receive browser notifications for your morning and evening routine'}
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  id="reminders"
                  type="checkbox"
                  checked={settings.reminders_enabled}
                  onChange={(e) => handleChange('reminders_enabled', e.target.checked)}
                  data-testid="reminders-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item toggle-item">
              <div className="toggle-info">
                <label htmlFor="moodSwitch">{isGerman ? 'Stimmung bei Frequenzwechsel erfragen' : 'Ask mood on frequency change'}</label>
                <p className="setting-hint">
                  {isGerman 
                    ? 'Nach jeder Frequenz-Nutzung wirst du gefragt, wie du dich fühlst (1-10 Skala). Dies hilft dem Begleiter, dich besser zu unterstützen.'
                    : 'After each frequency usage, you will be asked how you feel (1-10 scale). This helps the companion to support you better.'}
                </p>
              </div>
              <label className="toggle-switch">
                <input
                  id="moodSwitch"
                  type="checkbox"
                  checked={settings.mood_on_switch_enabled}
                  onChange={(e) => handleChange('mood_on_switch_enabled', e.target.checked)}
                  data-testid="mood-switch-toggle"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* PWA Installation - ganz am Ende */}
          <div className="settings-section">
            <h2 className="section-title">📱 {isGerman ? 'App installieren' : 'Install App'}</h2>
            <div className="pwa-install-section">
              <p className="setting-hint" style={{ marginBottom: '12px' }}>
                {isGerman 
                  ? 'Installiere ZENTA als App auf deinem Gerät für schnelleren Zugriff und Offline-Funktionen.'
                  : 'Install ZENTA as an app on your device for faster access and offline features.'}
              </p>
              <PWAInstallButton />
            </div>
          </div>

          {/* Save Button */}
          <div className="settings-actions">
            <button
              onClick={handleSave}
              className="btn-save-settings"
              disabled={saving}
              data-testid="save-settings-button"
            >
              {saving 
                ? (isGerman ? 'Speichern...' : 'Saving...') 
                : (isGerman ? '💾 Einstellungen speichern' : '💾 Save Settings')}
            </button>
          </div>
        </div>
        )}


        {activeTab === 'affiliate' && (
          <div className="affiliate-management">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>{isGerman ? 'Lade Affiliate-Daten...' : 'Loading affiliate data...'}</p>
              </div>
            ) : (
              <div className="settings-section">
                <h2 className="section-title">💸 {isGerman ? 'Dein Affiliate-Partner-Bereich' : 'Your Affiliate Partner Area'}</h2>
                <p className="section-hint">
                  {isGerman 
                    ? 'Du bist automatisch Teil des ZENTA-Affiliate-Programms! Teile deinen Link und verdiene 10 Coins + Provisionen für jeden geworbenen User.'
                    : 'You are automatically part of the ZENTA affiliate program! Share your link and earn 10 coins + commissions for each referred user.'}
                </p>
                
                {affiliateProfile && (
                  <div>
                    {/* Affiliate Links Section - Prominent at top */}
                    <div className="affiliate-links-section">
                      <h3>🔗 {isGerman ? 'Deine Affiliate-Links' : 'Your Affiliate Links'}</h3>
                      <p className="links-hint">{isGerman ? 'Hänge' : 'Append'} <code>?ref={affiliateProfile.affiliate_code}</code> {isGerman ? 'an jede ZENTA-URL an!' : 'to any ZENTA URL!'}</p>
                      
                      <div className="link-item">
                        <label>{isGerman ? 'Hauptlink (Registrierung):' : 'Main link (Registration):'}</label>
                        <div className="link-display">
                          <input
                            type="text"
                            value={`${window.location.origin}/register?ref=${affiliateProfile.affiliate_code}`}
                            readOnly
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/register?ref=${affiliateProfile.affiliate_code}`);
                              setSuccess(isGerman ? 'Link kopiert!' : 'Link copied!');
                              setTimeout(() => setSuccess(''), 2000);
                            }}
                            className="btn-copy"
                          >
                            📋
                          </button>
                        </div>
                      </div>
                      
                      <div className="link-item">
                        <label>{isGerman ? 'Preisseite:' : 'Pricing page:'}</label>
                        <div className="link-display">
                          <input
                            type="text"
                            value={`${window.location.origin}/pricing?ref=${affiliateProfile.affiliate_code}`}
                            readOnly
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/pricing?ref=${affiliateProfile.affiliate_code}`);
                              setSuccess(isGerman ? 'Link kopiert!' : 'Link copied!');
                              setTimeout(() => setSuccess(''), 2000);
                            }}
                            className="btn-copy"
                          >
                            📋
                          </button>
                        </div>
                      </div>
                      
                      <div className="link-item">
                        <label>{isGerman ? 'Dein Affiliate-Code:' : 'Your Affiliate Code:'}</label>
                        <div className="link-display">
                          <input
                            type="text"
                            value={affiliateProfile.affiliate_code}
                            readOnly
                            style={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(affiliateProfile.affiliate_code);
                              setSuccess(isGerman ? 'Code kopiert!' : 'Code copied!');
                              setTimeout(() => setSuccess(''), 2000);
                            }}
                            className="btn-copy"
                          >
                            📋
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Category & Commission Info */}
                    <div className="affiliate-info-card">
                      <div className="info-row">
                        <span className="info-label">{isGerman ? 'Deine Kategorie:' : 'Your Category:'}</span>
                        <span className="category-badge-large" style={{ 
                          background: (affiliateProfile.category?.color || '#CD7F32') + '20', 
                          color: affiliateProfile.category?.color || '#CD7F32', 
                          border: `2px solid ${(affiliateProfile.category?.color || '#CD7F32')}40` 
                        }}>
                          {affiliateProfile.category?.name || 'Bronze'}
                        </span>
                      </div>
                      
                      <div className="info-row">
                        <span className="info-label">{isGerman ? 'Provisions-Modell:' : 'Commission Model:'}</span>
                        <span className="provisions-text">
                          {affiliateProfile.category?.initial_percentage || 30}% {isGerman ? 'erste' : 'first'} {affiliateProfile.category?.initial_days || 30} {isGerman ? 'Tage' : 'days'} → {affiliateProfile.category?.lifetime_percentage || 10}% Lifetime
                        </span>
                      </div>
                      
                      <div className="info-row">
                        <span className="info-label">{isGerman ? 'Registrierungs-Bonus:' : 'Registration Bonus:'}</span>
                        <span className="bonus-text">🪙 10 {isGerman ? 'Coins pro geworbenem User' : 'Coins per referred user'}</span>
                      </div>
                    </div>

                    {/* Stats Overview */}
                    <div className="affiliate-stats-overview">
                      <div className="stat-card-small">
                        <div className="stat-icon">🖱️</div>
                        <div>
                          <div className="stat-value-small">{affiliateProfile.total_clicks || 0}</div>
                          <div className="stat-label-small">{isGerman ? 'Link-Klicks' : 'Link Clicks'}</div>
                        </div>
                      </div>
                      
                      <div className="stat-card-small">
                        <div className="stat-icon">👥</div>
                        <div>
                          <div className="stat-value-small">{affiliateProfile.total_referrals || 0}</div>
                          <div className="stat-label-small">{isGerman ? 'Registrierungen' : 'Registrations'}</div>
                        </div>
                      </div>
                      
                      <div className="stat-card-small">
                        <div className="stat-icon">💰</div>
                        <div>
                          <div className="stat-value-small">{(affiliateProfile.total_earnings || 0).toFixed(2)}€</div>
                          <div className="stat-label-small">{isGerman ? 'Gesamt-Provisionen' : 'Total Commissions'}</div>
                        </div>
                      </div>
                      
                      <div className="stat-card-small">
                        <div className="stat-icon">💵</div>
                        <div>
                          <div className="stat-value-small">{(affiliateProfile.available_balance || 0).toFixed(2)}€</div>
                          <div className="stat-label-small">{isGerman ? 'Verfügbar' : 'Available'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Payout Button - Only show if balance > 0 */}
                    {(affiliateProfile.available_balance || 0) > 0 && (
                      <div className="payout-section">
                        <button
                          onClick={() => {
                            // Check if payment details exist
                            if (!affiliateProfile.payment_method) {
                              setError(isGerman ? 'Bitte gib zuerst deine Zahlungsdaten ein' : 'Please enter your payment details first');
                              return;
                            }
                            setPayoutAmount((affiliateProfile.available_balance || 0).toFixed(2));
                            setShowPayoutModal(true);
                          }}
                          className="btn-request-payout"
                          disabled={(affiliateProfile.available_balance || 0) < 100}
                        >
                          💸 {isGerman ? 'Auszahlung anfordern' : 'Request Payout'}
                          {(affiliateProfile.available_balance || 0) < 100 && (
                            <span className="payout-hint"> ({isGerman ? 'ab 100€' : 'min. 100€'})</span>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Payment Details Form - Collapsible */}
                    <div className="payment-details-section">
                      <h3>🏦 {isGerman ? 'Zahlungsdaten' : 'Payment Details'} {affiliateProfile.payment_method ? (isGerman ? '(gespeichert)' : '(saved)') : (isGerman ? '(für Auszahlung eingeben)' : '(enter for payout)')}</h3>
                      
                      <div className="form-grid">
                        <div className="form-group">
                          <label>{isGerman ? 'Zahlungsmethode' : 'Payment Method'}</label>
                          <select
                            value={affiliateForm.payment_method || affiliateProfile.payment_method || 'iban'}
                            onChange={(e) => setAffiliateForm({...affiliateForm, payment_method: e.target.value})}
                          >
                            <option value="iban">{isGerman ? 'Banküberweisung (IBAN)' : 'Bank Transfer (IBAN)'}</option>
                            <option value="paypal">PayPal</option>
                          </select>
                        </div>
                        
                        {(affiliateForm.payment_method || affiliateProfile.payment_method || 'iban') === 'iban' && (
                          <div className="form-group">
                            <label>IBAN</label>
                            <input
                              type="text"
                              value={affiliateForm.iban || affiliateProfile.iban || ''}
                              onChange={(e) => setAffiliateForm({...affiliateForm, iban: e.target.value})}
                              placeholder="DE89 3704 0044 0532 0130 00"
                            />
                          </div>
                        )}
                        
                        {(affiliateForm.payment_method || affiliateProfile.payment_method) === 'paypal' && (
                          <div className="form-group">
                            <label>{isGerman ? 'PayPal E-Mail' : 'PayPal Email'}</label>
                            <input
                              type="email"
                              value={affiliateForm.paypal_email || affiliateProfile.paypal_email || ''}
                              onChange={(e) => setAffiliateForm({...affiliateForm, paypal_email: e.target.value})}
                              placeholder="deine@email.de"
                            />
                          </div>
                        )}
                        
                        <div className="form-group">
                          <label>{isGerman ? 'Kontoinhaber / Name' : 'Account Holder / Name'}</label>
                          <input
                            type="text"
                            value={affiliateForm.account_holder_name || affiliateProfile.account_holder_name || ''}
                            onChange={(e) => setAffiliateForm({...affiliateForm, account_holder_name: e.target.value})}
                            placeholder="Max Mustermann"
                          />
                        </div>
                      </div>

                      <button
                        onClick={handleBecomeAffiliate}
                        className="btn-save-payment"
                      >
                        💾 {isGerman ? 'Zahlungsdaten speichern' : 'Save Payment Details'}
                      </button>
                    </div>

                    {/* Referrals List */}
                    <div className="referrals-section">
                      <h3>👥 {isGerman ? 'Geworbene User' : 'Referred Users'} ({referrals.length})</h3>
                      
                      {referrals.length === 0 ? (
                        <div className="empty-state-small">
                          <p>{isGerman ? 'Du hast noch keine User geworben. Teile deinen Referral-Link!' : 'You haven\'t referred any users yet. Share your referral link!'}</p>
                        </div>
                      ) : (
                        <div className="referrals-table">
                          <table>
                            <thead>
                              <tr>
                                <th>{isGerman ? 'Name' : 'Name'}</th>
                                <th>{isGerman ? 'Registriert' : 'Registered'}</th>
                                <th>Status</th>
                                <th>{isGerman ? 'Verdient' : 'Earned'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {referrals.map((ref) => (
                                <React.Fragment key={ref.id}>
                                  <tr className="referral-row">
                                    <td>{ref.user_name}</td>
                                    <td>{new Date(ref.referred_at).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}</td>
                                    <td>
                                      <span className={`status-badge-small ${ref.status}`}>
                                        {ref.status === 'trial' && `🆓 ${isGerman ? 'Trial' : 'Trial'}`}
                                        {ref.status === 'converted' && `✅ ${isGerman ? 'Bezahlt' : 'Paid'}`}
                                        {ref.status === 'expired' && `❌ ${isGerman ? 'Abgelaufen' : 'Expired'}`}
                                      </span>
                                    </td>
                                    <td className="total-earned">
                                      <span className="earned-amount">{(ref.total_earned || 0).toFixed(2)}€</span>
                                    </td>
                                  </tr>
                                  {/* Earnings detail row */}
                                  {ref.earnings && ref.earnings.length > 0 && (
                                    <tr className="earnings-detail-row">
                                      <td colSpan="4">
                                        <div className="earnings-breakdown">
                                          {ref.earnings.map((e, idx) => (
                                            <div key={idx} className="earning-item">
                                              <span className="earning-product">{e.product_name || 'Subscription'}</span>
                                              <span className="earning-calc">
                                                {(e.product_price || 0).toFixed(2)}€ × {e.percentage_used}% = 
                                                <strong> {(e.amount || 0).toFixed(2)}€</strong>
                                              </span>
                                              <span className={`earning-type-badge ${e.earning_type}`}>
                                                {e.earning_type === 'initial' ? 'Initial' : 'Lifetime'}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Earnings History */}
                    <div className="earnings-section">
                      <h3>💰 {isGerman ? 'Provisions-Verlauf' : 'Commission History'}</h3>
                      
                      {earnings.length === 0 ? (
                        <div className="empty-state-small">
                          <p>{isGerman ? 'Noch keine Provisionen verdient. Deine geworbenen User müssen erst bezahlen.' : 'No commissions earned yet. Your referred users need to make a purchase first.'}</p>
                        </div>
                      ) : (
                        <div className="earnings-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Datum</th>
                                <th>Typ</th>
                                <th>Provision</th>
                                <th>Betrag</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {earnings.map((earning) => (
                                <tr key={earning.id}>
                                  <td>{new Date(earning.created_at).toLocaleDateString('de-DE')}</td>
                                  <td>
                                    <span className={`earning-type ${earning.earning_type}`}>
                                      {earning.earning_type === 'initial' ? '📅 Initial' : '♾️ Lifetime'}
                                    </span>
                                  </td>
                                  <td className="percentage-col">{earning.percentage_used}%</td>
                                  <td className="amount-col">{earning.amount.toFixed(2)}€</td>
                                  <td>
                                    <span className="status-badge-small approved">✅ Gutgeschrieben</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Payouts History */}
                    <div className="payouts-section">
                      <h3>💳 Auszahlungs-Verlauf</h3>
                      
                      {payouts.length === 0 ? (
                        <div className="empty-state-small">
                          <p>Noch keine Auszahlungen angefordert.</p>
                        </div>
                      ) : (
                        <div className="payouts-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Beantragt</th>
                                <th>Betrag</th>
                                <th>Methode</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payouts.map((payout) => (
                                <tr key={payout.id}>
                                  <td>{new Date(payout.requested_at).toLocaleDateString('de-DE')}</td>
                                  <td className="amount-col">{payout.amount.toFixed(2)}€</td>
                                  <td>
                                    {payout.payment_method === 'iban' ? '🏦 IBAN' : '💳 PayPal'}
                                  </td>
                                  <td>
                                    <span className={`payout-status ${payout.status}`}>
                                      {payout.status === 'requested' && '⏳ Beantragt'}
                                      {payout.status === 'approved' && '✅ Genehmigt'}
                                      {payout.status === 'paid' && '💰 Bezahlt'}
                                      {payout.status === 'rejected' && '❌ Abgelehnt'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Payout Modal */}
        {showPayoutModal && (
          <div className="modal-overlay" onClick={() => setShowPayoutModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>💸 Auszahlung anfordern</h2>
                <button onClick={() => setShowPayoutModal(false)} className="modal-close">×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Verfügbares Guthaben: <strong>{affiliateProfile?.available_balance.toFixed(2)}€</strong>
                </p>
                
                <div className="form-group">
                  <label>Auszahlungsbetrag (EUR)</label>
                  <input
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    min="100"
                    step="0.01"
                    placeholder="z.B. 150.00"
                  />
                  <p className="input-hint">Mindestbetrag: 100€</p>
                </div>
                
                {affiliateProfile && (
                  <div className="payout-info">
                    <p><strong>Auszahlung an:</strong></p>
                    <p>
                      {affiliateProfile.payment_method === 'iban' 
                        ? `🏦 IBAN: ${affiliateProfile.iban}`
                        : `💳 PayPal: ${affiliateProfile.paypal_email}`}
                    </p>
                    <p>{affiliateProfile.account_holder_name}</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowPayoutModal(false)} className="btn-cancel">Abbrechen</button>
                <button onClick={handleRequestPayout} className="btn-submit">Auszahlung beantragen</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cards' && (
          <div className="cards-management">
            {subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed ? (
              <div className="subscription-required-message">
                <div className="lock-icon">🔒</div>
                <h3>Testphase abgelaufen</h3>
                <p>Upgrade jetzt, um Proxy-Karten nutzen zu können</p>
                <button onClick={() => navigate('/pricing')} className="btn-upgrade">
                  Jetzt upgraden
                </button>
              </div>
            ) : (
              <div className="cards-section">
                <h3>🎴 Meine Proxy-Karten</h3>
                <p className="section-hint">
                  Aktiviere physische ZENTA-Karten und lade sie mit deinen Lieblings-Frequenzen auf
                </p>
                
                <button onClick={handleClaimCard} className="btn-claim-card">
                  + Karte hinzufügen (Code eingeben)
                </button>

                {subAccounts.length === 0 ? (
                  <p className="empty-message">Noch keine Karten aktiviert</p>
                ) : (
                  <div className="my-cards-list">
                    {subAccounts.map((card) => (
                      <div key={card.id} className="my-card-item">
                        <div className="card-code-display">🎴 {card.code}</div>
                        <div className="card-frequency-select">
                          <select
                            value={card.frequency_id || ''}
                            onChange={(e) => handleAssignFrequency(card.id, e.target.value || null)}
                          >
                            <option value="">Keine Frequenz</option>
                            {frequencies.map((freq) => (
                              <option key={freq.id} value={freq.id}>
                                {freq.icon} {freq.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => handleReleaseCard(card.id)}
                          className="btn-release-card"
                        >
                          🗑️ Freigeben
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'family' && subscriptionStatus && (
          <div className="family-management">
            <div className="family-section">
              <h3>📨 Einladungen erstellen</h3>
              <p className="section-hint">
                Du kannst bis zu 4 Freunde oder Familienmitglieder einladen ({subAccounts.length} von 4 Slots belegt)
              </p>
              
              {subAccounts.length < 4 && (
                <div className="invite-create">
                  <input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Name der Person (z.B. Sarah)"
                    className="invite-name-input"
                  />
                  <button
                    onClick={handleCreateInvitation}
                    className="btn-create-invite"
                    disabled={!inviteName.trim()}
                  >
                    ✉️ Einladung erstellen
                  </button>
                </div>
              )}

              {invitations.filter(i => i.status === 'pending').length > 0 && (
                <div className="invitations-list">
                  <h4>Offene Einladungen:</h4>
                  {invitations.filter(i => i.status === 'pending').map((inv) => (
                    <div key={inv.code} className="invitation-item">
                      <div className="invitation-info">
                        <strong>{inv.invited_name}</strong>
                        <div className="invitation-link">
                          {window.location.origin}/register?invite={inv.code}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/register?invite=${inv.code}`);
                          alert('Link kopiert!');
                        }}
                        className="btn-copy-link"
                      >
                        📋 Kopieren
                      </button>
                      <button
                        onClick={() => handleRevokeInvitation(inv.code)}
                        className="btn-revoke"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="family-section">
              <h3>👥 Aktive Sub-Accounts</h3>
              {subAccounts.length === 0 ? (
                <p className="empty-message">Noch keine Sub-Accounts</p>
              ) : (
                <div className="sub-accounts-list">
                  {subAccounts.map((sub) => (
                    <div key={sub.id} className="sub-account-item">
                      <div className="sub-info">
                        <div className="sub-name">{sub.name}</div>
                        <div className="sub-email">{sub.email}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveSubAccount(sub.id)}
                        className="btn-remove-sub"
                      >
                        🗑️ Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'practitioners' && (
          <div className="practitioners-management">
            {!user.is_practitioner && !user.roles?.includes('practitioner') && (
              <div className="settings-section become-practitioner-form">
                <h2 className="section-title">🩺 Als Therapeut registrieren</h2>
                <p className="section-hint">
                  Möchtest du selbst andere Menschen begleiten? Aktiviere deine Therapeuten-Rolle hier.
                </p>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Name / Praxisname *</label>
                    <input
                      type="text"
                      value={practitionerForm.practitioner_name}
                      onChange={(e) => setPractitionerForm({...practitionerForm, practitioner_name: e.target.value})}
                      placeholder="z.B. Dr. Maria Schmidt oder Heilpraxis Lichtblick"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Spezialisierung (optional)</label>
                    <input
                      type="text"
                      value={practitionerForm.specialization}
                      onChange={(e) => setPractitionerForm({...practitionerForm, specialization: e.target.value})}
                      placeholder="z.B. Energiearbeit, Meditation, Klangtherapie"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Firmenname (optional)</label>
                    <input
                      type="text"
                      value={practitionerForm.company_name}
                      onChange={(e) => setPractitionerForm({...practitionerForm, company_name: e.target.value})}
                      placeholder="z.B. Heilpraxis am See"
                    />
                  </div>
                </div>

                <button
                  onClick={handleBecomePractitioner}
                  className="btn-activate-practitioner"
                  disabled={!practitionerForm.practitioner_name.trim()}
                >
                  ✨ Therapeuten-Rolle aktivieren
                </button>
              </div>
            )}

            <div className="settings-section">
              <h2 className="section-title">🩺 Meine Therapeuten</h2>
              <p className="section-hint">
                Hier siehst du alle Therapeuten, die Zugriff auf deine Daten haben. 
                Du kannst den Zugriff jederzeit widerrufen.
              </p>

              {connectedPractitioners.length === 0 ? (
                <div className="empty-state-practitioners">
                  <div className="empty-icon">🌱</div>
                  <h3>Noch keine Therapeuten</h3>
                  <p>Du hast noch keinem Therapeuten Zugriff gewährt.</p>
                  <p className="help-text">
                    💡 Teile deine ZEN-ID (<strong>{user.zen_id}</strong>) mit deinem Therapeuten,
                    damit er dir eine Zugriffsanfrage senden kann.
                  </p>
                </div>
              ) : (
                <div className="practitioners-list">
                  {connectedPractitioners.map((conn) => (
                    <div key={conn.id} className="practitioner-card" data-testid="practitioner-card">
                      <div className="practitioner-header">
                        <div className="practitioner-avatar">
                          {conn.practitioner_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="practitioner-info">
                          <h3 className="practitioner-name">{conn.practitioner_name}</h3>
                          {conn.practitioner_specialization && (
                            <p className="practitioner-specialization">{conn.practitioner_specialization}</p>
                          )}
                          <p className="connected-since">
                            Verbunden seit: {new Date(conn.connected_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>

                      <div className="practitioner-permissions">
                        <h4>Gewährte Berechtigungen:</h4>
                        <div className="permissions-grid">
                          {conn.permissions.view_statistics && (
                            <div className="permission-badge-active">📊 Statistiken</div>
                          )}
                          {conn.permissions.view_journal && (
                            <div className="permission-badge-active">📖 Journal</div>
                          )}
                          {conn.permissions.view_mood_data && (
                            <div className="permission-badge-active">😊 Stimmung</div>
                          )}
                          {conn.permissions.recommend_frequencies && (
                            <div className="permission-badge-active">✨ Empfehlen</div>
                          )}
                          {conn.permissions.write_notes && (
                            <div className="permission-badge-active">📝 Notizen</div>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleRevokePractitionerAccess(conn.id, conn.practitioner_name)}
                        className="btn-revoke-access"
                        data-testid="revoke-access-button"
                      >
                        🚫 Zugriff widerrufen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </main>

      {/* Custom Dialogs */}
      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog(null)}
        />
      )}

      {promptDialog && (
        <PromptDialog
          title={promptDialog.title}
          message={promptDialog.message}
          placeholder={promptDialog.placeholder}
          onConfirm={promptDialog.onConfirm}
          onCancel={promptDialog.onCancel}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
      </div>
    </>
  );
}

export default Settings;
